import api from './api';
import i18n from '@/plugins/i18n';

const state = getDefaultState();

function getDefaultState() {
	return {
		reports: [],
		reportsByDuty: {},
		dutyReports: [],
		message: '',
		error: false,
		success: false,
	};
}

const getters = {
	formattedDutyReports: (state, getters, rootState, rootGetters) => {
		if (!state.dutyReports || !Array.isArray(state.dutyReports)) {
			return [];
		}

		return state.dutyReports.map((report) => {
			const enrichedReport = { ...report };
			const { 'users/getUserById': getUserById } = rootGetters;

			// Get creator information using the getUserById getter
			const creator = getUserById(report.createdBy);
			enrichedReport.createdByName = creator?.displayName;

			// Get decider information (only if status is approved or rejected)
			if ((report.status === 'approved' || report.status === 'rejected') && report.decidedBy) {
				const decider = getUserById(report.decidedBy);
				enrichedReport.decidedByName = decider?.displayName || null;
			}

			return enrichedReport;
		});
	},
};

const mutations = {
	setReports(state, { reports }) {
		state.reports = reports;
		const reportsByDuty = {};
		for (const report of reports) {
			if (reportsByDuty[report.duty] === undefined) {
				reportsByDuty[report.duty] = [];
			}
			reportsByDuty[report.duty].push(report);
		}
		state.reportsByDuty = reportsByDuty;
	},
	setReport(state, { reports }) {
		state.dutyReports = reports;
	},
	createReport(state, { report }) {
		state.reports.push(report);
		if (state.reportsByDuty[report.duty] === undefined) {
			state.reportsByDuty[report.duty] = [];
		}
		state.reportsByDuty[report.duty].push(report);
	},

	updateReport(state, { report }) {
		const reportIndex = state.reports.findIndex((r) => r.id === report.id);
		if (reportIndex !== -1) {
			state.reports.splice(reportIndex, 1, report);
		}

		if (state.reportsByDuty[report.duty]) {
			const dutyIndex = state.reportsByDuty[report.duty].findIndex((r) => r.id === report.id);
			if (dutyIndex !== -1) {
				state.reportsByDuty[report.duty].splice(dutyIndex, 1, report);
			}
		}
	},

	deleteReport(state, { report }) {
		const reportIndex = state.reports.findIndex((r) => r.id === report.id);
		if (reportIndex !== -1) {
			state.reports.splice(reportIndex, 1);
		}

		if (state.reportsByDuty[report.duty]) {
			const dutyIndex = state.reportsByDuty[report.duty].findIndex((r) => r.id === report.id);
			if (dutyIndex !== -1) {
				state.reportsByDuty[report.duty].splice(dutyIndex, 1);
			}
			if (state.reportsByDuty[report.duty].length === 0) {
				delete state.reportsByDuty[report.duty];
			}
		}
	},
	showError(state, { message }) {
		state.message = i18n.t(message);
		state.error = true;
	},
	showSuccess(state, { message }) {
		state.message = message;
		state.success = true;
	},
	resetError(state) {
		state.message = '';
		state.error = false;
	},
	hideSuccess(state) {
		state.message = '';
		state.success = false;
	},
};

const actions = {
	async loadReports({ state, commit, rootGetters, dispatch }, user) {
		try {
			const reports = await api.getReports(user).then((response) => response.data);
			commit('setReports', { reports });
		} catch (err) {
			const message = err.response?.data.message;
			commit('showError', { message });
		}
	},

	async loadReportsByDuty({ state, commit, rootGetters, dispatch }, duty) {
		try {
			const reports = await api.getReportsByDuty(duty).then((response) => response.data);
			commit('setReport', { reports });
		} catch (err) {
			const message = err.response?.data.message;
			commit('showError', { message });
		}
	},

	async loadReportByID({ state, commit }, reportID) {
		try {
			const response = await api.getReportsByID(reportID).then((response) => response.data);
			return response;
		} catch (err) {
			const message = err.response?.data.message;
			commit('showError', { message });
		}
	},

	async addNewReport({ state, commit, dispatch }, report) {
		try {
			const result = await api.createReport(report);
			await dispatch('loadReports');
			return result;
		} catch (err) {
			const message = err.response.data.message;
			await commit('showError', { message });
			window.setTimeout(() => {
				commit('hideError');
			}, 3000);
		}
	},

	async editReport({ state, commit }, { reportID, payload }) {
		try {
			const response = await api.editReport(reportID, payload);
			return response;
		} catch (err) {
			const message = err.response?.data.message;
			commit('showError', { message });
		}
	},

	async deleteReport({ state, commit, dispatch }, { reportID, changeComment }) {
		try {
			const response = await api.deleteReport(reportID, changeComment);
			await dispatch('loadReports');
			return response;
		} catch (err) {
			const message = err.response?.data.message;
			commit('showError', { message });
		}
	},

	resetError({ commit }) {
		commit('resetError');
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
